import { useState } from 'react';
import { Guest, RSVPs, RSVP, MenuType, MenuChoice, updateRSVPs } from '../../../services/api';
import { useTranslation } from 'react-i18next';

export const newRSVP = (): RSVP => ({
  name: '',
  menu_choice: {
    type: MenuType.Adult,
    starter: '',
    main: '',
    dessert: '',
    restrictions: '',
  },
  attending_ceremony: true,
  attending_reception: true,
});

export const rsvpFromGuest = (guest: Guest): RSVPs => {
  const useDefaultRSVP =
    guest.rsvps.length === 0 && (guest.attending === undefined || guest.attending === true);
  return {
    message: guest.message ?? '',
    attending: guest.attending ?? true,
    rsvps: useDefaultRSVP
      ? [
          {
            ...newRSVP(),
            name: guest.name,
          },
        ]
      : guest.rsvps,
  };
};

export interface RsvpFormProps {
  guest: Guest;
  token: string;
}

export function useRsvpForm({ guest, token }: RsvpFormProps) {
  const [formData, setFormData] = useState<RSVPs>(rsvpFromGuest(guest));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { t, i18n } = useTranslation();

  const isAttending = formData.attending ?? true;

  const menu = guest.menu?.[i18n.language] || guest.menu?.en;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    field: keyof RSVPs
  ) => {
    const { value } = e.target;
    const newFormData = {
      ...formData,
      [field]: value,
    };
    if (field === 'attending') {
      if (value === 'true') {
        newFormData[field] = true;
        newFormData.rsvps =
          newFormData.rsvps.length === 0 ? [{ ...newRSVP(), name: guest.name }] : newFormData.rsvps;
      } else if (value === 'false') {
        newFormData[field] = false;
        newFormData.rsvps = [];
      }
    }
    setFormData(newFormData);
    setError(null);
    setIsSuccess(false);
  };

  const handleRSVPChange = (index: number, field: keyof RSVP, value: boolean | string) => {
    const updatedRSVPs = [...formData.rsvps];
    updatedRSVPs[index] = {
      ...updatedRSVPs[index],
      [field]: value,
    };
    if (field === 'attending_reception') {
      if (value === true) {
        // add empty menu
        updatedRSVPs[index].menu_choice = newRSVP().menu_choice;
      } else {
        updatedRSVPs[index].menu_choice = undefined;
      }
    }
    setFormData({
      ...formData,
      rsvps: updatedRSVPs,
    });
    setError(null);
    setIsSuccess(false);
  };

  const handleMenuChoiceChange = (index: number, field: keyof MenuChoice, value: string) => {
    const updatedRSVPs = [...formData.rsvps];
    const updatedMenuChoice = {
      ...updatedRSVPs[index].menu_choice,
      [field]: value,
    } as MenuChoice;
    if (field === 'type' && value !== MenuType.Adult) {
      updatedMenuChoice.starter = '';
      updatedMenuChoice.main = '';
      updatedMenuChoice.dessert = '';
    }
    updatedRSVPs[index] = {
      ...updatedRSVPs[index],
      menu_choice: updatedMenuChoice,
    };
    setFormData({
      ...formData,
      rsvps: updatedRSVPs,
    });
    setError(null);
    setIsSuccess(false);
  };

  const addRSVP = () => {
    setFormData({
      ...formData,
      rsvps: [...formData.rsvps, newRSVP()],
    });
    setError(null);
    setIsSuccess(false);
  };

  const removeRSVP = (index: number) => {
    const updatedRSVPs = [...formData.rsvps];
    updatedRSVPs.splice(index, 1);
    setFormData({
      ...formData,
      rsvps: updatedRSVPs,
    });
    setError(null);
    setIsSuccess(false);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setIsSuccess(false);

    try {
      await updateRSVPs(token, formData);
      setIsSuccess(true);
    } catch (error) {
      setError(error instanceof Error ? error.message : t('submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    data: {
      formData,
      menu,
      error,
      isSubmitting,
      isSuccess,
      isAttending,
    },
    actions: {
      handleChange,
      handleRSVPChange,
      handleMenuChoiceChange,
      addRSVP,
      removeRSVP,
      t,
      handleSubmit,
    },
  };
}
